<template>
    <themed-modal
        name="tour-form-modal"
        :title="modalTitle"
        size="full"
        :show-footer="true"
        @closed="$emit('close')"
    >
        <!-- Wizard/Tabs for both Create and Edit Mode -->
        <div class="tour-edit-tabs">
            <!-- Tab Navigation -->
            <div class="tour-edit-tabs__nav">
                <themed-button
                    v-for="tab in availableTabs" 
                    :key="tab.id"
                    :variant="activeTab === tab.id ? 'default' : 'ghost'"
                    :disabled="!canAccessTab(tab.id)"
                    @click="switchToTab(tab.id)"
                    class="tour-edit-tabs__tab"
                >
                    <theme-icon :icon="tab.icon" type="icons" />
                    <span>{{ tab.label }}</span>
                    <theme-icon v-if="!canAccessTab(tab.id)" icon="lock" type="icons" class="tour-edit-tabs__tab-lock" />
                </themed-button>
            </div>

            <!-- Tab Content -->
            <div class="tour-edit-tabs__content">
                <!-- Details Tab -->
                <div v-if="activeTab === 'details'" class="tour-edit-tabs__pane">
                    <themed-form
                        :fields="visibleFormFields"
                        :model-value="form"
                        :errors="errors"
                        layout="grid"
                        :columns="12"
                        @input="form = $event"
                    />
                </div>

                <!-- Schedule Tab -->
                <div v-if="activeTab === 'schedule'" class="tour-edit-tabs__pane">
                    <tour-schedule-calendar
                        v-if="fullTour"
                        :tour="fullTour"
                        :available-comps="availableComps"
                        @schedule-saved="handleScheduleSaved"
                    />
                    <div v-else class="tour-edit-tabs__empty-state">
                        <theme-icon icon="calendar" type="icons" />
                        <p>Please save the tour first to access the schedule builder.</p>
                    </div>
                </div>

                <!-- Attendees Tab -->
                <div v-if="activeTab === 'attendees'" class="tour-edit-tabs__pane">
                    <div v-if="!fullTour" class="tour-edit-tabs__empty-state">
                        <theme-icon icon="users" type="icons" />
                        <p>Please save the tour first to manage attendees.</p>
                    </div>
                    <div v-else class="tour-attendees-tab">
                        <!-- Two Column Layout -->
                        <div class="tour-attendees-tab__columns">
                            <!-- Left Column: Invite Form -->
                            <div class="tour-attendees-tab__left-column">
                                <themed-card>
                                    <template #header>
                                        <h4 class="tour-attendees-tab__section-title">Invite Attendees</h4>
                                    </template>
                                    
                                    <!-- Invite Form (individual mode) -->
                                    <div v-if="inviteMethod === 'individual'" class="tour-invite-form">
                                        <!-- Invitation Method Radio -->
                                        <div class="themed-form__field">
                                            <label class="themed-form-field__label">Invitation Method</label>
                                            <themed-radio-group
                                                :value="inviteMethod"
                                                name="inviteMethod"
                                                :options="[{value:'individual',label:'Individual'},{value:'group',label:'From Group'}]"
                                                @input="inviteMethod = $event"
                                            />
                                        </div>
                                        <!-- Email Addresses -->
                                        <div class="themed-form__field">
                                            <label class="themed-form-field__label">Email Addresses</label>
                                            <themed-textarea
                                                :value="inviteEmails"
                                                placeholder="email1@example.com, email2@example.com"
                                                :rows="3"
                                                @input="inviteEmails = $event"
                                            />
                                            <span class="themed-form-field__help">Enter email addresses separated by commas</span>
                                        </div>
                                        <!-- Existing Users -->
                                        <div v-if="availableUsers.length > 0" class="themed-form__field">
                                            <label class="themed-form-field__label">Existing Users</label>
                                            <themed-multi-select
                                                :value="selectedUserObjects"
                                                :options="userOptions"
                                                placeholder="Search and select users to invite"
                                                :searchable="true"
                                                :close-on-select="false"
                                                track-by="value"
                                                label="label"
                                                @input="selectedUserObjects = $event"
                                            />
                                            <span class="themed-form-field__help">Search and select users to invite</span>
                                        </div>
                                    </div>
                                    <!-- Group mode -->
                                    <div v-else class="tour-invite-form">
                                        <div class="themed-form__field">
                                            <label class="themed-form-field__label">Invitation Method</label>
                                            <themed-radio-group
                                                :value="inviteMethod"
                                                name="inviteMethod"
                                                :options="[{value:'individual',label:'Individual'},{value:'group',label:'From Group'}]"
                                                @input="inviteMethod = $event"
                                            />
                                        </div>
                                        <div class="themed-form__field">
                                            <label class="themed-form-field__label">Select Group</label>
                                            <themed-select
                                                :value="selectedGroupId"
                                                :options="groupOptions"
                                                placeholder="Select a group"
                                                @input="selectedGroupId = $event; onGroupSelected($event)"
                                            />
                                        </div>
                                        <div v-if="selectedGroupMembers.length > 0" class="themed-form__field">
                                            <label class="themed-form-field__label">Select Members to Invite ({{ selectedGroupMemberObjects.length }} / {{ selectedGroupMembers.length }})</label>
                                            <themed-multi-select
                                                :value="selectedGroupMemberObjects"
                                                :options="selectedGroupMemberOptions"
                                                placeholder="Select members to invite"
                                                :searchable="true"
                                                :close-on-select="false"
                                                track-by="value"
                                                label="label"
                                                @input="selectedGroupMemberObjects = $event"
                                            />
                                        </div>
                                    </div>

                                    <!-- Manage Groups Button (for group mode) -->
                                    <div v-if="inviteMethod === 'group'" class="tour-attendees-tab__manage-groups">
                                        <themed-button
                                            variant="outline"
                                            size="sm"
                                            @click="showGroupManager = true"
                                        >
                                            <theme-icon icon="users" type="icons" />
                                            Manage Groups
                                        </themed-button>
                                    </div>

                                    <template #footer>
                                        <div class="tour-attendees-tab__actions">
                                            <div class="tour-attendees-tab__actions-info">
                                                <span v-if="canSendInvites" class="tour-attendees-tab__invite-summary">
                                                    <template v-if="inviteMethod === 'group'">
                                                        Inviting {{ selectedGroupMemberObjects.length }} member{{ selectedGroupMemberObjects.length !== 1 ? 's' : '' }} from group
                                                    </template>
                                                    <template v-else-if="selectedUserObjects.length > 0 && inviteEmails.trim().length > 0">
                                                        Inviting {{ selectedUserObjects.length }} user{{ selectedUserObjects.length !== 1 ? 's' : '' }} and email addresses
                                                    </template>
                                                    <template v-else-if="selectedUserObjects.length > 0">
                                                        Inviting {{ selectedUserObjects.length }} user{{ selectedUserObjects.length !== 1 ? 's' : '' }}
                                                    </template>
                                                    <template v-else>
                                                        Inviting via email addresses
                                                    </template>
                                                </span>
                                            </div>
                                            <themed-button
                                                variant="default"
                                                size="default"
                                                @click="sendInvites"
                                                :disabled="!canSendInvites || sendingInvites"
                                            >
                                                {{ sendingInvites ? 'Sending...' : 'Send Invitations' }}
                                            </themed-button>
                                        </div>
                                    </template>
                                </themed-card>

                                <!-- Group Manager Modal -->
                                <attendee-group-manager
                                    v-if="showGroupManager"
                                    :tour-id="fullTour.id"
                                    :available-users="availableUsers"
                                    @groups-updated="loadGroups"
                                    @close="showGroupManager = false"
                                />
                            </div>

                            <!-- Right Column: Current Attendees -->
                            <div class="tour-attendees-tab__right-column">
                                <themed-card>
                                    <template #header>
                                        <div class="tour-attendees-tab__header-with-count">
                                            <h4 class="tour-attendees-tab__section-title">Current Attendees</h4>
                                            <themed-badge v-if="fullTour?.attendees?.length" variant="secondary">
                                                {{ fullTour.attendees.length }}
                                            </themed-badge>
                                        </div>
                                    </template>
                                    
                                    <div v-if="!fullTour || !fullTour.attendees || !fullTour.attendees.length" class="tour-attendees-tab__empty-state">
                                        <p>No attendees yet. Invite people using the form on the left.</p>
                                    </div>

                                    <div v-else class="tour-attendees-tab__attendees-list">
                                        <div 
                                            v-for="attendee in fullTour.attendees" 
                                            :key="attendee.id"
                                            class="tour-attendees-tab__attendee-row"
                                        >
                                            <div class="tour-attendees-tab__attendee-info">
                                                <div class="tour-attendees-tab__attendee-avatar">
                                                    {{ getInitials(attendee.display_name) }}
                                                </div>
                                                <div class="tour-attendees-tab__attendee-details">
                                                    <div class="tour-attendees-tab__attendee-name">{{ attendee.display_name }}</div>
                                                    <div class="tour-attendees-tab__attendee-email">
                                                        {{ attendee.display_email }}
                                                        <span v-if="attendee.group_name" class="tour-attendees-tab__group-name">
                                                            ({{ attendee.group_name }})
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="tour-attendees-tab__attendee-actions-row">
                                                <themed-badge :variant="getStatusBadgeVariant(attendee.invitation_status)">
                                                    {{ formatInvitationStatus(attendee.invitation_status) }}
                                                </themed-badge>
                                                <themed-button
                                                    v-if="attendee.user_id !== currentUserId"
                                                    variant="destructive"
                                                    size="sm"
                                                    @click="removeAttendee(attendee)"
                                                    title="Remove attendee"
                                                >
                                                    <theme-icon icon="trash" type="icons" />
                                                </themed-button>
                                            </div>
                                        </div>
                                    </div>
                                </themed-card>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </div>

        <template #footer>
            <div class="tour-wizard-footer">
                <!-- Left side: Cancel button -->
                <div class="tour-wizard-footer__left">
                    <themed-button
                        type="button"
                        @click="closeModal"
                        variant="secondary"
                    >
                        Cancel
                    </themed-button>
                </div>

                <!-- Right side: Navigation buttons -->
                <div class="tour-wizard-footer__right">
                    <!-- Previous button -->
                    <themed-button
                        v-if="!isFirstTab"
                        type="button"
                        @click.prevent="handlePreviousClick"
                        variant="outline"
                        :disabled="isNavigating"
                    >
                        <theme-icon icon="chevron-left" type="icons" />
                        Previous
                    </themed-button>

                    <!-- Next button (only show if not on last accessible tab) -->
                    <themed-button
                        v-if="!isLastAccessibleTab"
                        type="button"
                        @click.prevent="handleNextClick"
                        variant="default"
                        :disabled="isNavigating"
                    >
                        Next
                        <theme-icon icon="chevron-right" type="icons" />
                    </themed-button>

                    <!-- Save/Update button (only show on details tab) -->
                    <themed-button
                        v-if="activeTab === 'details'"
                        type="button"
                        @click="saveTour"
                        variant="default"
                        :disabled="saving"
                    >
                        {{ saving ? 'Saving...' : (isEditing ? 'Update Tour' : 'Create Tour') }}
                    </themed-button>
                </div>
            </div>
        </template>
    </themed-modal>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedBadge from '../ThemedBadge.vue';
import ThemedCard from '../ThemedCard.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedMultiSelect from '../form/ThemedMultiSelect.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TourScheduleCalendar from './TourScheduleCalendar.vue';
import AttendeeGroupManager from './AttendeeGroupManager.vue';

export default {
    name: 'TourFormModal',

    components: {
        ThemedModal,
        ThemedButton,
        ThemedBadge,
        ThemedCard,
        ThemedForm,
        ThemedMultiSelect,
        ThemedSelect,
        ThemeIcon,
        TourScheduleCalendar,
        AttendeeGroupManager,
    },

    props: {
        tour: {
            type: Object,
            default: null,
        },
        projectId: {
            type: Number,
            required: true,
        },
        availableComps: {
            type: Array,
            default: () => [],
        },
        availableSites: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            form: {
                name: '',
                description: '',
                site_id: null,
                date: null,
                status: 'draft',
                assigned_broker_id: null, // For admin to assign broker
            },
            errors: {},
            saving: false,
            activeTab: 'details',
            fullTour: null,
            inviteEmails: '',
            selectedUsers: [],
            selectedUserObjects: [], // For multiselect v-model
            availableUsers: [],
            availableBrokers: [], // List of brokers for admin
            sendingInvites: false,
            projectSites: [], // Sites belonging to this project
            loadingSites: false,
            isNavigating: false, // Navigation lock to prevent rapid clicks
            inviteMethod: 'individual', // 'individual' or 'group'
            selectedGroupId: null,
            selectedGroupMembers: [],
            selectedGroupMemberOptions: [], // Store the formatted options
            selectedGroupMemberObjects: [], // Array of {value, label} objects for multiselect
            groups: [],
            showGroupManager: false,
            inviteFormData: {
                inviteMethod: 'individual',
                inviteEmails: '',
                selectedUserObjects: [],
                selectedGroupId: null,
                selectedGroupMemberObjects: [], // Changed from selectedGroupMemberIds to objects
            },
            formFields: [
                {
                    name: 'name',
                    type: 'input',
                    label: 'Tour Name',
                    required: true,
                    placeholder: 'Enter tour name',
                    col: 4,
                    validation: {
                        required: true,
                        message: 'Tour name is required'
                    }
                },
                {
                    name: 'description',
                    type: 'textarea',
                    label: 'Description',
                    placeholder: 'Enter tour description',
                    rows: 3,
                    col: 4
                },
                {
                    name: 'site_id',
                    type: 'select',
                    label: 'Site',
                    placeholder: 'Select a site',
                    options: [],
                    helpText: '',
                    col: 4
                },
                {
                    name: 'date',
                    type: 'input',
                    inputType: 'datetime-local',
                    label: 'Tour Date & Time',
                    required: true,
                    placeholder: 'Select date and time',
                    helpText: 'Select the date and time for the tour',
                    col: 4,
                    validation: {
                        required: true,
                        message: 'Tour date and time is required'
                    },
                    componentProps: {
                        min: new Date().toISOString().slice(0, 16) // Prevent past dates
                    }
                },
                {
                    name: 'assigned_broker_id',
                    type: 'select',
                    label: 'Assign Broker',
                    placeholder: 'Select a broker to assign this tour',
                    options: [],
                    required: true,
                    helpText: 'The assigned broker will be automatically added as an attendee',
                    col: 4,
                    validation: {
                        required: true,
                        message: 'Please select a broker to assign to this tour'
                    }
                },
                {
                    name: 'status',
                    type: 'select',
                    label: 'Status',
                    options: [
                        { value: 'draft', label: 'Draft' },
                        { value: 'active', label: 'Active' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'cancelled', label: 'Cancelled' },
                    ],
                    helpText: 'Status auto-updates: Draft → Active (24hrs before tour) → Completed (after tour date, once attendees are invited)',
                    col: 4
                }
            ]
        };
    },

    computed: {
        isEditing() {
            return !!this.tour || !!this.fullTour;
        },
        
        modalTitle() {
            // Check both tour and fullTour for the name
            const tourName = this.tour?.name || this.fullTour?.name;
            if (this.isEditing && tourName) {
                return `Edit Tour: ${tourName}`;
            }
            return this.isEditing ? 'Edit Tour' : 'Create New Tour';
        },
        
        isAdmin() {
            return this.$gate && this.$gate.isAdmin();
        },
        
        isBroker() {
            return this.$gate && this.$gate.isBroker();
        },
        
        currentUserId() {
            return this.$gate && this.$gate.user ? this.$gate.user.id : null;
        },
        
        visibleFormFields() {
            // Filter fields based on user role
            return this.formFields.filter(field => {
                if (field.name === 'assigned_broker_id') {
                    // Only show "Assign Broker" field for admin users
                    // Brokers are automatically assigned to themselves
                    return this.isAdmin;
                }
                return true;
            });
        },
        
        compOptions() {
            return this.availableComps.map(comp => ({
                id: comp.pivot?.id || comp.id,
                label: this.getCompLabel(comp),
                value: comp.pivot?.id || comp.id,
                comp: comp
            }));
        },

        siteOptions() {
            return this.projectSites.map(site => ({
                value: site.id,
                label: this.getSiteLabel(site)
            }));
        },

        siteFieldHelpText() {
            if (this.projectSites.length === 0) {
                return 'No sites available for this project';
            }
            
            if (this.projectSites.length === 1) {
                return 'This project has only one site (auto-selected)';
            }
            
            return `Select from ${this.projectSites.length} project sites`;
        },

        brokerOptions() {
            return this.availableBrokers.map(broker => ({
                value: broker.id,
                label: broker.client_name || broker.client?.name 
                    ? `${broker.name} (${broker.client_name || broker.client?.name})`
                    : broker.name
            }));
        },

        tabs() {
            return [
                { id: 'details', label: 'Tour Form', icon: 'edit' },
                { id: 'schedule', label: 'Schedule Builder', icon: 'calendar' },
                { id: 'attendees', label: 'Attendees', icon: 'users' },
            ];
        },

        availableTabs() {
            // In create mode, show all tabs but some will be disabled
            // In edit mode, show all tabs
            return this.tabs;
        },

        currentTabIndex() {
            return this.tabs.findIndex(tab => tab.id === this.activeTab);
        },

        isFirstTab() {
            return this.currentTabIndex === 0;
        },

        isLastAccessibleTab() {
            // If editing, last tab is always accessible
            if (this.isEditing) {
                return this.currentTabIndex === this.tabs.length - 1;
            }
            // If creating, only details tab is accessible
            return this.activeTab === 'details';
        },

        currentAttendeeId() {
            if (!this.fullTour || !this.fullTour.attendees) return null;
            const currentUser = window.Laravel?.user;
            if (!currentUser) return null;
            const attendee = this.fullTour.attendees.find(a => a.user_id === currentUser.id);
            return attendee?.id || null;
        },

        canSendInvites() {
            if (this.inviteMethod === 'group') {
                return this.selectedGroupId && this.selectedGroupMemberObjects.length > 0;
            }
            return this.inviteEmails.trim().length > 0 || this.selectedUserObjects.length > 0;
        },

        userOptions() {
            // Format users for ThemedMultiSelect
            return this.availableUsers.map(user => ({
                label: user.email ? `${user.name} (${user.email})` : user.name,
                value: user.id,
            }));
        },

        groupOptions() {
            return this.groups.map(group => ({
                value: group.id,
                label: `${group.name} (${group.members_count || group.members?.length || 0} members)`,
            }));
        },

        inviteFormFields() {
            const fields = [];

            // Invite method radio buttons (now using ThemedRadioGroup)
            fields.push({
                name: 'inviteMethod',
                type: 'radio',
                label: 'Invitation Method',
                options: [
                    { value: 'individual', label: 'Individual' },
                    { value: 'group', label: 'From Group' }
                ],
                componentProps: {
                    layout: 'horizontal'
                }
            });

            // Individual mode fields
            if (this.inviteFormData.inviteMethod === 'individual') {
                fields.push({
                    name: 'inviteEmails',
                    type: 'textarea',
                    label: 'Email Addresses',
                    placeholder: 'email1@example.com, email2@example.com',
                    rows: 4,
                    helpText: 'Enter email addresses separated by commas'
                });

                if (this.availableUsers.length > 0) {
                    fields.push({
                        name: 'selectedUserObjects',
                        type: 'multiselect',
                        label: 'Existing Users',
                        placeholder: 'Search users by name or email',
                        helpText: 'Search and select users to invite',
                        options: this.userOptions, // Move options to field level
                        componentProps: {
                            searchable: true,
                            closeOnSelect: false,
                            trackBy: 'value',  // Use camelCase
                            label: 'label'
                        }
                    });
                }
            }

            // Group mode fields
            if (this.inviteFormData.inviteMethod === 'group') {
                fields.push({
                    name: 'selectedGroupId',
                    type: 'select',
                    label: 'Select Group',
                    placeholder: 'Select a group',
                    options: this.groupOptions,
                    helpText: 'Choose a group to invite members from'
                });

                // Multi-select for member selection
                if (this.selectedGroupMembers.length > 0) {
                    console.log('Creating multiselect field with options:', this.selectedGroupMemberOptions);
                    console.log('Current selected values:', this.inviteFormData.selectedGroupMemberObjects);
                    
                    fields.push({
                        name: 'selectedGroupMemberObjects',
                        type: 'multiselect',
                        label: `Select Members to Invite (${this.inviteFormData.selectedGroupMemberObjects?.length || 0} / ${this.selectedGroupMembers.length})`,
                        placeholder: 'Select members to invite',
                        helpText: 'Choose which group members to invite to this tour',
                        options: this.selectedGroupMemberOptions, // Move options to field level
                        componentProps: {
                            searchable: true,
                            closeOnSelect: false,
                            trackBy: 'value',  // Use camelCase
                            label: 'label'
                        }
                    });
                }
            }

            return fields;
        },
    },

    watch: {
        inviteFormData: {
            handler(newVal) {
                console.log('inviteFormData changed:', newVal);
                
                // Sync inviteFormData with individual data properties
                this.inviteMethod = newVal.inviteMethod;
                this.inviteEmails = newVal.inviteEmails;
                this.selectedUserObjects = newVal.selectedUserObjects;
                
                if (newVal.selectedGroupId !== this.selectedGroupId) {
                    console.log('Group ID changed from', this.selectedGroupId, 'to', newVal.selectedGroupId);
                    this.selectedGroupId = newVal.selectedGroupId;
                    this.onGroupSelected(newVal.selectedGroupId);
                }
                
                // Sync selectedGroupMemberObjects
                if (newVal.selectedGroupMemberObjects !== this.selectedGroupMemberObjects) {
                    console.log('selectedGroupMemberObjects changed:', newVal.selectedGroupMemberObjects);
                    this.selectedGroupMemberObjects = newVal.selectedGroupMemberObjects || [];
                }
            },
            deep: true
        },

        // Sync back to inviteFormData when individual properties change
        inviteMethod(newVal) {
            if (this.inviteFormData.inviteMethod !== newVal) {
                this.inviteFormData.inviteMethod = newVal;
            }
        },
        inviteEmails(newVal) {
            if (this.inviteFormData.inviteEmails !== newVal) {
                this.inviteFormData.inviteEmails = newVal;
            }
        },
        selectedUserObjects(newVal) {
            if (this.inviteFormData.selectedUserObjects !== newVal) {
                this.inviteFormData.selectedUserObjects = newVal;
            }
            // Also sync with selectedUsers array
            this.selectedUsers = newVal.map(user => user.value || user.id);
        },
        selectedGroupMemberObjects(newVal) {
            if (this.inviteFormData.selectedGroupMemberObjects !== newVal) {
                this.inviteFormData.selectedGroupMemberObjects = newVal;
            }
        },
    },

    mounted() {
        this.$modal.show('tour-form-modal');
        
        // Load project sites first
        this.loadProjectSites();

        // Load groups
        this.loadGroups();
        
        // Auto-assign broker if user is a broker creating a new tour
        // This happens silently in the background - broker won't see the field
        if (this.isBroker && !this.isEditing && this.currentUserId) {
            this.form.assigned_broker_id = this.currentUserId;
            console.log('Auto-assigned broker to tour:', this.currentUserId);
        }
        
        // Load brokers for admin (do this before populating form for editing)
        if (this.isAdmin) {
            this.loadBrokers().then(() => {
                // After brokers are loaded, populate form if editing
                if (this.tour) {
                    this.populateFormForEditing();
                }
            });
        } else if (this.tour) {
            // If not admin, just populate the form
            this.populateFormForEditing();
        }
    },

    methods: {
        canAccessTab(tabId) {
            // Details tab is always accessible
            if (tabId === 'details') {
                return true;
            }
            // Other tabs require the tour to be saved (editing mode with fullTour loaded)
            return this.isEditing && this.fullTour;
        },

        switchToTab(tabId) {
            if (this.canAccessTab(tabId)) {
                this.activeTab = tabId;
            }
        },

        handleNextClick(event) {
            // Prevent default and stop propagation
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            
            // Call nextTab
            this.nextTab();
        },

        handlePreviousClick(event) {
            // Prevent default and stop propagation
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            
            // Call previousTab
            this.previousTab();
        },

        nextTab() {
            // Prevent multiple rapid clicks
            if (this.isNavigating) {
                console.log('Navigation already in progress, ignoring click');
                return;
            }
            
            console.log('Next tab clicked, current tab:', this.activeTab);
            this.isNavigating = true;
            
            const currentIndex = this.currentTabIndex;
            if (currentIndex < this.tabs.length - 1) {
                // Find next accessible tab
                for (let i = currentIndex + 1; i < this.tabs.length; i++) {
                    if (this.canAccessTab(this.tabs[i].id)) {
                        console.log('Switching to tab:', this.tabs[i].id);
                        this.activeTab = this.tabs[i].id;
                        break;
                    }
                }
            }
            
            // Reset navigation lock after a short delay
            this.$nextTick(() => {
                setTimeout(() => {
                    this.isNavigating = false;
                    console.log('Navigation lock released');
                }, 300);
            });
        },

        previousTab() {
            // Prevent multiple rapid clicks
            if (this.isNavigating) {
                console.log('Navigation already in progress, ignoring click');
                return;
            }
            
            console.log('Previous tab clicked, current tab:', this.activeTab);
            this.isNavigating = true;
            
            const currentIndex = this.currentTabIndex;
            if (currentIndex > 0) {
                // Find previous accessible tab
                for (let i = currentIndex - 1; i >= 0; i--) {
                    if (this.canAccessTab(this.tabs[i].id)) {
                        console.log('Switching to tab:', this.tabs[i].id);
                        this.activeTab = this.tabs[i].id;
                        break;
                    }
                }
            }
            
            // Reset navigation lock after a short delay
            this.$nextTick(() => {
                setTimeout(() => {
                    this.isNavigating = false;
                    console.log('Navigation lock released');
                }, 300);
            });
        },

        populateFormForEditing() {
            if (!this.tour) return;
            
            // Format date for datetime-local input (YYYY-MM-DDTHH:mm)
            let formattedDate = null;
            if (this.tour.date) {
                formattedDate = moment(this.tour.date).format('YYYY-MM-DDTHH:mm');
            }

            this.form = {
                name: this.tour.name,
                description: this.tour.description || '',
                site_id: this.tour.site_id || null,
                date: formattedDate,
                status: this.tour.status,
                assigned_broker_id: this.tour.creator_id || null, // Set the assigned broker (tour creator)
            };

            // Load full tour data for tabs
            this.loadFullTour().then(() => {
                this.loadAvailableUsers();
            });
        },
        
        getSiteLabel(site) {
            // Try to get name from building first
            if (site.building) {
                if (site.building.building_name) {
                    return site.building.building_name;
                }
                if (site.building.address_line_1) {
                    return site.building.address_line_1;
                }
            }
            
            // Fallback to site properties
            if (site.name) {
                return site.name;
            }
            if (site.site_name) {
                return site.site_name;
            }
            
            // Last resort
            return `Site #${site.id}`;
        },

        async loadProjectSites() {
            this.loadingSites = true;
            
            try {
                const response = await axios.get(`/projects/${this.projectId}/sites`);
                this.projectSites = response.data;
                
                // Update site field options and help text
                const siteField = this.formFields.find(f => f.name === 'site_id');
                if (siteField) {
                    siteField.options = this.siteOptions;
                    siteField.helpText = this.siteFieldHelpText;
                    
                    // Auto-select if only one site and not editing
                    if (this.projectSites.length === 1 && !this.isEditing) {
                        this.form.site_id = this.projectSites[0].id;
                    }
                    
                    // Keep placeholder consistent
                    if (this.projectSites.length === 0) {
                        siteField.placeholder = 'No sites available';
                    } else {
                        siteField.placeholder = 'Select a site';
                    }
                }
            } catch (error) {
                console.error('Failed to load project sites:', error);
                this.projectSites = [];
                
                const siteField = this.formFields.find(f => f.name === 'site_id');
                if (siteField) {
                    siteField.helpText = 'Failed to load project sites';
                }
            } finally {
                this.loadingSites = false;
            }
        },
        async loadBrokers() {
            try {
                const response = await axios.get('/users', {
                    headers: {
                        'Accept': 'application/json'
                    }
                });
                
                const users = response.data.users || response.data;
                
                // Filter to only brokers (across all clients for admin)
                this.availableBrokers = users.filter(user => {
                    return user.role && user.role.name === 'broker';
                });
                
                // Update broker field options
                const brokerField = this.formFields.find(f => f.name === 'assigned_broker_id');
                if (brokerField) {
                    brokerField.options = this.brokerOptions;
                }
            } catch (error) {
                console.error('Failed to load brokers:', error);
                this.availableBrokers = [];
            }
        },

        async loadFullTour() {
            const tourId = this.fullTour?.id || this.tour?.id;
            if (!tourId) return;
            
            try {
                const response = await axios.get(`/tours/${tourId}`);
                this.fullTour = response.data;
            } catch (error) {
                console.error('Error loading full tour:', error);
            }
        },

        async loadFullTourById(tourId) {
            try {
                const response = await axios.get(`/tours/${tourId}`);
                this.fullTour = response.data;
            } catch (error) {
                console.error('Error loading full tour:', error);
            }
        },

        async loadAvailableUsers() {
            const tourId = this.fullTour?.id || this.tour?.id;
            if (!tourId) return;
            
            try {
                // First ensure we have the full tour data with project
                if (!this.fullTour) {
                    await this.loadFullTour();
                }
                
                // Load users using the web route with JSON headers
                // The UserController already filters by client based on auth user
                const response = await axios.get('/users', {
                    headers: {
                        'Accept': 'application/json'
                    }
                });
                
                // Extract users array from response
                const users = response.data.users || response.data;
                
                // Get current user ID
                const currentUserId = window.Laravel?.user?.id;
                
                // Filter out:
                // 1. Users who are already attendees
                // 2. The current logged-in user (they're already the creator/attendee)
                this.availableUsers = users.filter(user => {
                    // Exclude current user
                    if (user.id === currentUserId) {
                        return false;
                    }
                    
                    // Exclude existing attendees
                    const isAttendee = this.fullTour?.attendees?.some(attendee => {
                        return attendee.user_id === user.id;
                    });
                    
                    return !isAttendee;
                });
            } catch (error) {
                console.error('Failed to load users:', error);
                this.availableUsers = [];
            }
        },

        async sendInvites() {
            this.sendingInvites = true;
            try {
                let payload = {};

                if (this.inviteMethod === 'group') {
                    // Invite from group
                    payload.group_id = this.selectedGroupId;
                    // Extract IDs from multiselect objects (array of {value, label} objects)
                    payload.group_member_ids = Array.isArray(this.selectedGroupMemberObjects) 
                        ? this.selectedGroupMemberObjects.map(item => item.value || item)
                        : [];
                    
                    console.log('Sending group invitations with member IDs:', payload.group_member_ids);
                } else {
                    // Individual mode
                    const emails = this.inviteEmails
                        .split(',')
                        .map(e => e.trim())
                        .filter(e => e.length > 0);

                    // Extract IDs directly from the form data objects — more reliable than the watcher-synced selectedUsers array
                    const selectedUserIds = (this.selectedUserObjects || [])
                        .map(user => user.value || user.id)
                        .filter(id => id != null);

                    if (selectedUserIds.length > 0) {
                        payload.user_ids = selectedUserIds;
                    }
                    if (emails.length > 0) {
                        payload.emails = emails;
                    }
                }

                console.log('Sending invitations:', payload);

                // Use fullTour.id if available (after creating), otherwise fall back to tour.id (when editing)
                const tourId = this.fullTour?.id || this.tour?.id;
                if (!tourId) {
                    throw new Error('Tour ID not found. Please save the tour first.');
                }

                const response = await axios.post(`/tours/${tourId}/invite`, payload);

                // Clear form
                this.inviteEmails = '';
                this.selectedUsers = [];
                this.selectedUserObjects = [];
                this.selectedGroupId = null;
                this.selectedGroupMembers = [];
                this.selectedGroupMemberOptions = [];
                this.selectedGroupMemberObjects = [];
                
                // Reset inviteFormData
                this.inviteFormData.selectedGroupMemberObjects = [];
                this.inviteFormData.selectedGroupId = null;
                
                // Reload data to refresh both lists
                await this.loadFullTour();
                await this.loadAvailableUsers();
                
                if (this.$toast) {
                    this.$toast.success(response.data.message || 'Invitations sent successfully');
                }
            } catch (error) {
                console.error('Failed to send invitations:', error.response?.data);
                if (this.$toast) {
                    this.$toast.error(error.response?.data?.message || 'Failed to send invitations');
                }
            } finally {
                this.sendingInvites = false;
            }
        },

        async removeAttendee(attendee) {
            if (!confirm(`Remove ${attendee.display_name} from this tour?`)) {
                return;
            }

            try {
                const tourId = this.fullTour?.id || this.tour?.id;
                await axios.delete(`/tours/${tourId}/attendees/${attendee.id}`);
                await this.loadFullTour();
                await this.loadAvailableUsers();
                
                if (this.$toast) {
                    this.$toast.success('Attendee removed successfully');
                }
            } catch (error) {
                if (this.$toast) {
                    this.$toast.error('Failed to remove attendee');
                }
            }
        },

        handleScheduleSaved() {
            if (this.$toast) {
                this.$toast.success('Schedule saved successfully!');
            }
            this.loadFullTour();
        },

        getCompLabel(comp) {
            if (comp.building && comp.building.building_name) {
                return comp.building.building_name;
            }
            if (comp.building && comp.building.address_line_1) {
                return comp.building.address_line_1;
            }
            if (comp.building_name) {
                return comp.building_name;
            }
            if (comp.name) {
                return comp.name;
            }
            return `Comp #${comp.id}`;
        },
        
        closeModal() {
            this.$modal.hide('tour-form-modal');
        },
        
        async saveTour() {
            this.saving = true;
            this.errors = {};
            
            // Frontend validation: Admin must select a broker
            if (this.isAdmin && !this.form.assigned_broker_id) {
                this.errors = {
                    assigned_broker_id: ['Please select a broker to assign to this tour']
                };
                
                if (this.$toast) {
                    this.$toast.error('Please select a broker to assign to this tour');
                }
                
                this.saving = false;
                return;
            }
            
            // Frontend validation: Tour name is required
            if (!this.form.name || this.form.name.trim() === '') {
                this.errors = {
                    name: ['Tour name is required']
                };
                
                if (this.$toast) {
                    this.$toast.error('Tour name is required');
                }
                
                this.saving = false;
                return;
            }
            
            // Frontend validation: Tour date is required
            if (!this.form.date) {
                this.errors = {
                    date: ['Tour date and time is required']
                };
                
                if (this.$toast) {
                    this.$toast.error('Tour date and time is required');
                }
                
                this.saving = false;
                return;
            }
            
            // Frontend validation: Tour date cannot be in the past
            if (this.form.date) {
                const selectedDate = moment(this.form.date);
                const now = moment();
                
                if (selectedDate.isBefore(now)) {
                    this.errors = {
                        date: ['Tour date cannot be in the past']
                    };
                    
                    if (this.$toast) {
                        this.$toast.error('Tour date cannot be in the past');
                    }
                    
                    this.saving = false;
                    return;
                }
            }
            
            try {
                // Prepare payload
                const payload = {
                    name: this.form.name,
                    description: this.form.description,
                    site_id: this.form.site_id || null,
                    status: this.form.status,
                    date: this.form.date ? moment(this.form.date).format('YYYY-MM-DD HH:mm:ss') : null,
                };
                
                // Add assigned_broker_id
                // For admin: use the selected broker from the form
                // For broker: use their own ID (auto-assigned in mounted)
                if (this.form.assigned_broker_id) {
                    payload.assigned_broker_id = this.form.assigned_broker_id;
                    console.log('Adding assigned_broker_id to payload:', payload.assigned_broker_id);
                }
                
                console.log('Final payload:', payload);
                
                if (this.isEditing) {
                    await axios.put(`/tours/${this.tour.id}`, payload);
                } else {
                    const response = await axios.post(`/projects/${this.projectId}/tours`, payload);
                    
                    // After creating, switch to edit mode by loading the created tour
                    if (response.data && response.data.id) {
                        // Load the full tour data to enable edit mode
                        await this.loadFullTourById(response.data.id);
                        
                        // Load available users for attendees tab
                        await this.loadAvailableUsers();
                        
                        // Reload groups now that we have a tour ID
                        await this.loadGroups();
                        
                        // Update the tour prop to enable edit mode
                        this.$emit('tour-created', response.data);
                        
                        // Show success message
                        if (this.$toast) {
                            this.$toast.success('Tour created successfully! You can now add schedule and attendees.');
                        }
                        
                        // Don't close modal, allow user to continue to next tabs
                        return;
                    }
                }
                
                this.$emit('saved');
                this.closeModal();
            } catch (error) {
                console.error('Error saving tour:', error);
                console.error('Error response:', error.response);
                
                if (error.response && error.response.data && error.response.data.errors) {
                    this.errors = error.response.data.errors;
                }
                
                if (this.$toast) {
                    this.$toast.error('Failed to save tour');
                }
            } finally {
                this.saving = false;
            }
        },

        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },

        formatInvitationStatus(status) {
            const statuses = {
                pending: 'Pending',
                accepted: 'Accepted',
                declined: 'Declined'
            };
            return statuses[status] || status;
        },

        getStatusBadgeVariant(status) {
            const variants = {
                pending: 'outline',
                accepted: 'default',
                declined: 'destructive'
            };
            return variants[status] || 'outline';
        },

        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString();
        },

        async loadGroups() {
            // Groups are now globally accessible, not scoped to a specific tour or client
            try {
                const response = await axios.get('/tour-groups');
                this.groups = response.data;
            } catch (error) {
                console.error('Failed to load groups:', error);
                this.groups = [];
            }
        },

        onGroupSelected(groupId) {
            console.log('onGroupSelected called with groupId:', groupId);
            
            if (!groupId) {
                this.selectedGroupMembers = [];
                this.selectedGroupMemberOptions = [];
                this.selectedGroupMemberObjects = [];
                this.inviteFormData.selectedGroupMemberObjects = [];
                return;
            }

            const group = this.groups.find(g => g.id === groupId);
            console.log('Found group:', group);
            
            if (group && group.members) {
                this.selectedGroupMembers = group.members;
                console.log('Group members:', group.members);
                
                // Create member options and store them
                const memberOptions = group.members.map(member => ({
                    value: member.id,
                    label: `${member.display_name || member.attendee_name} (${member.attendee_email})`,
                    email: member.attendee_email
                }));
                
                // Set the options
                this.selectedGroupMemberOptions = memberOptions;
                
                // Clear any previous selections - let user choose manually
                this.selectedGroupMemberObjects = [];
                this.inviteFormData.selectedGroupMemberObjects = [];
                
                console.log('Member options created:', memberOptions);
                console.log('Selections cleared - user can now manually select members');
            }
        },
    },
};
</script>
